import { useEffect, useMemo } from "react";
import classNames from "classnames";
import { useTogglePopup } from "hooks/useTogglePopup";
import { GET_POPUP_TYPES } from "constants/popupConfig";
import { getLocalStorageItem } from "utils/localStorage";
import { STORAGE_MODAL_FORTUNE_WHEEL_SEEN } from "constants/variables";
import Rewards from "./Rewards";
import Header from "./Header";
import { FortuneWheel } from "./FortuneWheel";
import { useConfigWheel } from "../queries/useConfigWheel";
import { useFortuneWheelStore } from "../store/fortuneWheel";
import { FORTUNE_WHEEL_STEPS } from "../type/FortuneWheel";
import WheelSkeleton from "./FortuneWheel/components/WheelSkeleton";
import { useWheelAvailability } from "../queries/useWheelAvailable";

const FortuneWheelContainer = () => {
  const cfg = useConfigWheel();
  const availability = useWheelAvailability();

  const { openPopup } = useTogglePopup();

  const sections = useFortuneWheelStore((s) => s.sectionList);
  const nextAt = useFortuneWheelStore((s) => s.nextAvailableAt);
  const step = useFortuneWheelStore((s) => s.step);

  const setSectionList = useFortuneWheelStore((s) => s.setSectionList);
  const clear = useFortuneWheelStore((s) => s.clear);
  const setError = useFortuneWheelStore((s) => s.setError);

  useEffect(() => {
    const raw = getLocalStorageItem(STORAGE_MODAL_FORTUNE_WHEEL_SEEN);
    const isSeen = raw === "true";

    if (!raw || !isSeen) {
      localStorage.setItem(STORAGE_MODAL_FORTUNE_WHEEL_SEEN, "false");
      openPopup(GET_POPUP_TYPES.popup.fortuneWheel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cfg.isSuccess && cfg.data && Array.isArray(cfg.data.sections)) {
      setSectionList(cfg.data);
      setError("");
    }
  }, [cfg.isSuccess, cfg.data, setSectionList, setError]);

  useEffect(() => {
    if (cfg.isError) {
      setError(cfg.error?.message || "Failed to load wheel config");
    }
  }, [cfg.isError, cfg.error, setError]);

  useEffect(() => {
    if (
      step === FORTUNE_WHEEL_STEPS.SUCCESS ||
      step === FORTUNE_WHEEL_STEPS.FAILED
    ) {
      setTimeout(() => openPopup(GET_POPUP_TYPES.popup.fortuneWheelWin), 100);
      availability.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const hasValidSections = Array.isArray(sections) && sections.length > 0;

  const isLoading =
    (cfg.isPending && !hasValidSections) || availability.isPending;

  const isError =
    cfg.isError ||
    (!cfg.isPending && !hasValidSections) ||
    availability.isError;

  const FortuneWheelPageClass = classNames("FortuneWheelPage", {
    // eslint-disable-next-line camelcase
    FortuneWheelPage__error: isError,
  });

  useEffect(() => {
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = useMemo(() => {
    if (isError) {
      return (
        <>
          <div className="FortuneWheelPage__errorBox">
            Failed to load wheel config
          </div>
          <Rewards isLoading />
        </>
      );
    }

    if (isLoading) {
      return (
        <>
          <WheelSkeleton />
          <Rewards isLoading />
        </>
      );
    }

    return (
      <>
        <FortuneWheel sections={sections} nextAt={nextAt} step={step} />
        <Rewards />
      </>
    );
  }, [isError, isLoading, sections, nextAt, step]);

  return (
    <div className={FortuneWheelPageClass}>
      <Header />
      <div className="FortuneWheelPage__content">{content}</div>
    </div>
  );
};

export default FortuneWheelContainer;
